/**
 * Parse a money amount that may use a dot or comma decimal separator,
 * thousands separators, and stray text/currency ("10 Eur", "€10,50").
 * Returns a finite number, or null if it isn't a number.
 * Mirrors the backend `parseAmount` so the client and server agree.
 *   "10" -> 10 · "10,50" -> 10.5 · "10.50" -> 10.5 · "10 Eur" -> 10 · "abc" -> null
 */
export const parseAmount = (input: unknown): number | null => {
  if (typeof input === 'number') {
    return Number.isFinite(input) ? input : null;
  }
  if (input === null || input === undefined) {
    return null;
  }
  let s = String(input)
    .trim()
    .replace(/[^0-9.,-]/g, ''); // drop "Eur", "€", spaces, letters
  if (!s || s === '-') {
    return null;
  }
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma !== -1 || lastDot !== -1) {
    const decimalSep = lastComma > lastDot ? ',' : '.';
    const thousandsSep = decimalSep === ',' ? '.' : ',';
    s = s.split(thousandsSep).join('');
    s = s.split(decimalSep).join('.');
  }
  const value = parseFloat(s);
  return Number.isFinite(value) ? value : null;
};

/** True if the number has at most 2 decimal places (money precision). */
export const hasMaxTwoDecimals = (value: number): boolean =>
  Math.abs(value * 100 - Math.round(value * 100)) < 1e-9;
